<template>
  <v-sheet
    class="devtools-warnings !fixed top-[118px] bottom-0 w-[25vw] min-w-[300px] !z-10 -right-[max(25vw,300px)] !transition-all"
    :class="{ '!right-0': showPanel }"
    color="warning"
  >
    <v-fab
      v-show="showFAB"
      icon
      color="warning"
      class="!absolute top-8 -left-16 z-10"
      @click="togglePanel"
    >
      <v-badge color="error" :content="warnings.length">
        <v-icon
          v-if="showPanel"
          key="collapse"
          icon="mdi-chevron-right"
          size="x-large"
        />
        <v-icon
          v-else
          key="alert"
          icon="mdi-alert"
          class="!w-8 !h-8"
        />
      </v-badge>
    </v-fab>
    <div class="text-lg fw-medium pt-2 px-4 sticky top-0">
      Schema Warnings
    </div>
    <div class="overflow-y-auto px-4 max-h-[calc(100%-2rem)]">
      <div
        v-for="warning of warnings"
        :key="warning.id"
        class="my-2 text-sm"
      >
        <v-row
          class="align-center"
          @click="toggleRow(warning.id)"
          @mouseover="highlightField(warning.local)"
          @mouseleave="highlightField()"
        >
          <v-col cols="11">
            <p class="font-weight-bold">
              <v-chip
                class="mr-1 px-3"
                color="error"
                variant="elevated"
                size="x-small"
                v-text="warning.type.toUpperCase()"
              />
              <a
                v-if="warning.local"
                class="underline cursor-pointer hover:text-blue-500"
                @click="scrollField(warning.local)"
              >
                {{ warning.title }}
              </a>
              <span v-else v-text="warning.title" />
            </p>
            <p v-if="warning.description" v-text="warning.description" />
          </v-col>
          <v-col cols="1">
            <v-btn
              v-if="warning.context"
              size="sm"
              variant="text"
              :icon="rows[warning.id] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            />
          </v-col>
        </v-row>
        <code
          v-if="warning.context && rows[warning.id]"
          class="block bg-gray-600 w-full mt-2 pa-2 text-white whitespace-pre-wrap rounded"
          v-text="warning.context"
        />
        <v-divider class="mt-2" />
      </div>
    </div>
  </v-sheet>
</template>

<script lang="ts">
import { computed, defineComponent, inject, reactive, ref, watch } from 'vue'

import { CONTROLLER_KEY } from '@/plugins/devtools/shared'

import type { FormContextLocal } from '@/composables/formContext'

export default defineComponent({
  name: 'DevtoolsWarnings',

  setup () {
    const controller = inject(CONTROLLER_KEY)!
    const warnings = computed(() => (controller.currentConfigWarnings.toReversed() ?? []).reverse())

    const showFAB = computed(() => warnings.value.length > 0)
    watch(showFAB, () => {
      showPanel.value = false
    })

    const showPanel = ref(false)
    function togglePanel (): void {
      showPanel.value = !showPanel.value
    }

    const rows = reactive<Record<string, boolean>>({})
    function toggleRow (id: string): void {
      rows[id] = !rows[id]
    }

    function highlightField (localContext?: FormContextLocal): void {
      if (!localContext) {
        // if no local context is provided, remove any existing highlights
        const highlightedFields = document.querySelectorAll('.devtools-highlighted-field')
        highlightedFields.forEach((field) => {
          field.classList.remove('devtools-highlighted-field')
        })
        return
      }
      const { sectionIndex, subsectionIndex, fieldIndex } = localContext
      const key = [sectionIndex, subsectionIndex, fieldIndex].filter((i) => i !== undefined).join('-')
      const field = document.querySelector(`[data-key="${key}"]`)
      // highlight and scroll to the field
      if (field) {
        field.classList.add('devtools-highlighted-field')
      }
    }

    function scrollField (localContext: FormContextLocal): void {
      const { sectionIndex, subsectionIndex, fieldIndex } = localContext ?? {}
      const key = [sectionIndex, subsectionIndex, fieldIndex].filter((i) => i !== undefined).join('-')
      const field = document.querySelector(`[data-key="${key}"]`)
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    return {
      warnings,
      showFAB,
      showPanel,
      togglePanel,
      rows,
      toggleRow,
      scrollField,
      highlightField,
    }
  },
})
</script>
